type Id = number | string | null | undefined;

const LAST = Number.MAX_SAFE_INTEGER;

/**
 * Categories in the order the owner arranged them in POS (index ascending).
 * A category without an index goes last; ties keep their original order.
 */
export const sortCategoriesByIndex = <T extends { index?: number | null }>(categories: T[] = []): T[] =>
  categories
    .map((item, position) => ({ item, position }))
    .sort((a, b) => (a.item.index ?? LAST) - (b.item.index ?? LAST) || a.position - b.position)
    .map(o => o.item);

/**
 * Groups services by the given category order. The API already returns services sorted by
 * order_number, which is numbered per category, so the incoming order is kept inside each
 * category. Services whose category is not in the list go last.
 */
export const sortServicesByCategoryOrder = <S>(
  services: S[] = [],
  categoryIds: Id[],
  getCategoryId: (service: S) => Id,
): S[] => {
  const rankById = new Map(categoryIds.map((id, rank) => [String(id), rank]));
  const rankOf = (service: S) => rankById.get(String(getCategoryId(service))) ?? LAST;
  return services
    .map((item, position) => ({ item, position, rank: rankOf(item) }))
    .sort((a, b) => a.rank - b.rank || a.position - b.position)
    .map(o => o.item);
};
